'use client';
import React, { FC, useRef, useState } from 'react';
import { TypeReplacementPartsSubcomponentsPageData } from '@/app/[locale]/(shop)/account/replacement-parts/[product]/[component]/fetchPageData';
import {
  TypeModalFields,
  TypeReplacementPartsFields,
} from '@/core/generated-types';
import { SiblingsProductCard } from '../Siblings';
import { TypeSubscriberShopProduct } from '@/lib/api/productCatalog/types';
import { TypeSubscriberShopPageTranslations } from '@/utils/translations/types/TypeSubscriberShopPageTranslations';
import generateTranslationsObject from '@/utils/translations/generateTranslationObject';
import Heading, {
  HeadingTag,
  HeadingSize,
} from '@/components/ui/Heading/Heading';
import { Link } from '@/navigation';
import useLocale from '@/hooks/useLocale';
import { getUnpurchasableProducts } from './utils';
import ReplacementPartsTaggedProductsList from './ReplacementPartsTaggedProductsList';
import SiblingsErrorModal from '../Siblings/SiblingsErrorModal';
import useCartContext from '@/context/CartContext';
import { useIsVisible } from '@/hooks/useIsVisible';
import ReplacementPartsBreadcrumbTree from './ReplacementPartsBreadcrumbTree';
import { ITEM_ORIGINS } from '@/core/constants';
import { buildCartUrl } from '@/lib/cart/helpers';
interface ReplacementPartsSubcomponentsProps
  extends TypeReplacementPartsFields {
  data: TypeReplacementPartsSubcomponentsPageData;
}

const ReplacementPartsSubcomponents: FC<ReplacementPartsSubcomponentsProps> = (
  props,
) => {
  const {
    data: { products, inventoryStatusCopy, parentProduct, parentComponent },
    translations,
    siblingsErrorModal,
  } = props;
  const [addedProducts, setAddedProducts] = useState<
    TypeSubscriberShopProduct[]
  >([]);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const { cart } = useCartContext();
  const primaryCtaRef = useRef(null);
  const isPrimaryCtaVisible = useIsVisible(primaryCtaRef);

  const t = generateTranslationsObject(
    translations?.fields,
  ) as TypeSubscriberShopPageTranslations;
  const locale = useLocale();
  const cartUrl = buildCartUrl(locale);

  const unpurchasableProducts = getUnpurchasableProducts(products);

  const subscriberShopProducts = products?.filter(
    (product) => product.shopifyProduct,
  );

  const parentComponentAgeRange =
    parentComponent?.metadata.data?.age_tagline ||
    parentComponent?.display_age_range;

  return (
    <>
      <ReplacementPartsBreadcrumbTree
        rootText="Find Parts"
        rootUrl="/account/replacement-parts"
        parentProduct={parentProduct}
        parentComponent={parentComponent}
      />

      {parentComponent ? (
        <>
          <Heading
            tag={HeadingTag.H1}
            size={HeadingSize.H1}
            classes="mt-4 md:mt-8"
            text={parentComponent.name}
          />
          <p className="text-xl">{parentComponentAgeRange}</p>
        </>
      ) : null}

      <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-12 md:mt-12 md:grid-cols-2 lg:grid-cols-3">
        {subscriberShopProducts
          ?.map((subscriberShopProduct) => {
            const {
              product: { external_id, metadata },
            } = subscriberShopProduct;
            return (
              !metadata.data?.replacement_shop_unpurchasable && (
                <SiblingsProductCard
                  key={external_id}
                  subscriberShopProduct={subscriberShopProduct}
                  addedProducts={addedProducts}
                  handleAddedProducts={setAddedProducts}
                  translations={t}
                  inventoryStatusCopy={inventoryStatusCopy}
                  openErrorModal={() => setIsErrorModalOpen(true)}
                  itemOrigin={ITEM_ORIGINS.REPLACEMENT_PART_SHOP}
                />
              )
            );
          })
          .filter(Boolean)}
      </div>
      {subscriberShopProducts?.filter(
        (product) =>
          !product.product.metadata.data?.replacement_shop_unpurchasable,
      ).length === 0 && (
        <div className="mt-8 text-center">
          <p className="text-lg text-gray-600">
            {t.productsNotAvailableMessage ||
              "Oops! These products aren't available right now. We're working to bring them back soon."}
          </p>
        </div>
      )}
      <ReplacementPartsTaggedProductsList
        label={t.unpurchasableProductsLabel}
        products={unpurchasableProducts}
      />
      <div className="my-10 pt-4">
        <div className="flex flex-col items-end" ref={primaryCtaRef}>
          <div
            className={`flex flex-col items-end bg-white ${
              !isPrimaryCtaVisible
                ? 'fixed bottom-0 left-0 right-0 z-50 w-auto p-6 shadow-sticky-cta md:static'
                : 'w-full'
            }`}
          >
            <Link
              href={cartUrl}
              className="btn-primary btn-responsive btn h-auto w-full md:w-[460px]"
              aria-disabled={cart?.totalQuantity === 0}
            >
              {t.reviewYourOrderCta}
            </Link>
          </div>
          <Link
            href="/account/replacement-parts"
            className="btn-secondary btn-responsive btn my-4 h-auto w-full md:w-[460px]"
          >
            {t.continueShoppingPartsCta}
          </Link>
        </div>
      </div>

      <SiblingsErrorModal
        isOpen={isErrorModalOpen}
        handleClose={() => setIsErrorModalOpen(false)}
        translations={t}
        {...(siblingsErrorModal.fields as TypeModalFields)}
      />
    </>
  );
};

export default ReplacementPartsSubcomponents;
